// CR-8 — per-citizen entity model. Each owned citizen is an individual record
// { id, type, name, role, educated, bornAt } living on the active kingdom's
// `citizens` roster. The roster is the RPG/identity layer: it is kept in sync
// with the authoritative per-type COUNTS (seasonManager.getGoonsOwned), which
// still drive income, combat, and the field visuals. So nothing about the
// economy changes — we just give the populace names, trades, and a registry.
//
// Promotions (peasant → Farmer / Merchant / Artisan) cost no coin and raise
// civic Honor (honor.js reads promotedCount). Sending a citizen to school pays
// the one-time CitizenEducationPayout (moved here from a bare counter).

import { Events, emit } from '../ui/eventBus.js'
import {
  GoonTypes,
  GoonConfig,
  CitizenRoles,
  CitizenNamePool,
  CitizenEducationPayout,
} from '../config/balance.js'
import { active, persist } from './state.js'
import * as season from './seasonManager.js' // runtime-only (counts + earnWealth)

let idSeq = 0
const nextId = () => `c_${Date.now().toString(36)}_${(idSeq++).toString(36)}`

function ensureRoster(k) {
  if (!Array.isArray(k.citizens)) k.citizens = []
  return k.citizens
}

function pickName(roster) {
  const used = new Set(roster.map((c) => c.name))
  const free = CitizenNamePool.filter((n) => !used.has(n))
  if (free.length) return free[Math.floor(Math.random() * free.length)]
  // Pool exhausted → append a numeric suffix to a random base name.
  const base = CitizenNamePool[Math.floor(Math.random() * CitizenNamePool.length)]
  let n = 2
  while (used.has(`${base} ${n}`)) n += 1
  return `${base} ${n}`
}

function makeCitizen(type, roster) {
  return { id: nextId(), type, name: pickName(roster), role: null, educated: false, bornAt: Date.now() }
}

// When the count of a type shrinks (combat loss, lineage consumption), remove
// the least-invested citizen first so named/promoted/educated cats persist.
function pickRemovable(group) {
  return (
    group.find((c) => !c.role && !c.educated) ??
    group.find((c) => !c.role) ??
    group.find((c) => !c.educated) ??
    group[group.length - 1]
  )
}

// Reconcile the roster to the current per-type counts. Returns the live roster.
export function syncRoster() {
  const k = active()
  const roster = ensureRoster(k)
  let changed = false
  for (const type of Object.values(GoonTypes)) {
    const target = season.getGoonsOwned(type)
    let group = roster.filter((c) => c.type === type)
    while (group.length < target) {
      const c = makeCitizen(type, roster)
      roster.push(c)
      group.push(c)
      changed = true
    }
    while (group.length > target) {
      const victim = pickRemovable(group)
      roster.splice(roster.indexOf(victim), 1)
      group = group.filter((c) => c !== victim)
      changed = true
    }
  }
  if (changed) persist()
  return roster
}

// A name for a new patient: one of the roster's citizens of that type who is not already a patient (so a name is a real citizen).
export function patientName(goonId, taken = new Set()) {
  const free = syncRoster().filter((c) => c.type === goonId && !taken.has(c.name))
  return free.length ? free[Math.floor(Math.random() * free.length)].name : `${GoonConfig[goonId]?.name ?? 'Citizen'}`
}
// A citizen who has died: out of the roster by name, so the Citizens panel loses THAT name (call before adjustGoonsOwned(−1)).
export function removeCitizenNamed(goonId, name) {
  const roster = ensureRoster(active())
  const i = roster.findIndex((c) => c.type === goonId && c.name === name)
  if (i >= 0) { roster.splice(i, 1); persist() }
}

// A synced snapshot for the UI (copy so callers can't mutate the store).
export function getCitizens() {
  return syncRoster().map((c) => ({ ...c }))
}

export function countByType(type) {
  return syncRoster().filter((c) => c.type === type).length
}

export function total() {
  return syncRoster().length
}

export function promotedCount() {
  return syncRoster().filter((c) => !!c.role).length
}

export function educatedCount() {
  return syncRoster().filter((c) => c.educated).length
}

// Promote a citizen to a trade role — civic Honor, no coin. Pass role=null to
// clear the assignment. Returns true if it changed.
export function promoteCitizen(id, role) {
  if (role != null && !CitizenRoles[role]) return false
  const roster = syncRoster()
  const c = roster.find((x) => x.id === id)
  if (!c || c.role === role) return false
  c.role = role
  persist()
  emit(Events.CitizenPromoted, { id, role })
  return true
}

// Graduate one citizen from a School: mark the first un-educated citizen
// (peasants first — they're the field workers) and pay the one-time bonus.
// Returns { ok, type, payout, name } so callers can surface it.
export function educateOne() {
  const roster = syncRoster()
  const order = [GoonTypes.PEASANT_CAT, GoonTypes.SCRIBE_CAT, GoonTypes.NOBLE_CAT]
  let target = null
  for (const type of order) {
    target = roster.find((c) => c.type === type && !c.educated)
    if (target) break
  }
  if (!target) target = roster.find((c) => !c.educated)
  if (!target) return { ok: false }
  target.educated = true
  const payout = CitizenEducationPayout[target.type] ?? 0
  if (payout > 0) season.earnWealth(payout)
  persist()
  return { ok: true, type: target.type, payout, name: target.name }
}

// Display helper — a short label like "Peasant Cat" for a type.
export function typeLabel(type) {
  return GoonConfig[type]?.name ?? type
}
